export interface NormalizeCostOptions {
  /** Value returned when the input is empty or invalid and fallbackToDefault is true. */
  defaultValue?: number;
  /** When true, empty or invalid input falls back to the defaultValue instead of an empty string. */
  fallbackToDefault?: boolean;
  /** When true, textual inputs that cannot be parsed into a number are preserved as-is. */
  preserveTextOnNaN?: boolean;
}

export interface NormalizedCostResult {
  /** Value safe to store in itinerary slots or post payloads. */
  storageValue: number | string | '';
  /** String representation safe to render in the UI. */
  displayValue: string;
  /** Numeric representation when the value could be parsed, otherwise null. */
  numericValue: number | null;
  /** Indicates whether the resulting value is numeric. */
  isNumeric: boolean;
  /** True when the default value was used due to empty or invalid input. */
  usedDefault: boolean;
  /** True when the incoming value was empty or whitespace. */
  isEmpty: boolean;
}

const DEFAULT_COST_VALUE = 1000;

export function normalizeCost(
  value: unknown,
  options: NormalizeCostOptions = {}
): NormalizedCostResult {
  const {
    defaultValue = DEFAULT_COST_VALUE,
    fallbackToDefault = false,
    preserveTextOnNaN = true,
  } = options;

  const applyDefault = (isEmpty: boolean): NormalizedCostResult => {
    if (!fallbackToDefault) {
      return {
        storageValue: '',
        displayValue: '',
        numericValue: null,
        isNumeric: false,
        usedDefault: false,
        isEmpty,
      };
    }

    const normalized = Number.isInteger(defaultValue)
      ? Math.round(defaultValue)
      : Number(defaultValue.toFixed(2));

    return {
      storageValue: normalized,
      displayValue: normalized.toString(),
      numericValue: normalized,
      isNumeric: true,
      usedDefault: true,
      isEmpty,
    };
  };

  if (value === null || value === undefined) {
    return applyDefault(true);
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return applyDefault(true);
    }
    const normalizedNumber = Number.isInteger(value)
      ? Math.round(value)
      : Number(value.toFixed(2));
    return {
      storageValue: normalizedNumber,
      displayValue: normalizedNumber.toString(),
      numericValue: normalizedNumber,
      isNumeric: true,
      usedDefault: false,
      isEmpty: false,
    };
  }

  const raw = String(value);
  const trimmed = raw.trim();

  if (trimmed === '') {
    return applyDefault(true);
  }

  const sanitized = trimmed.replace(/[^0-9.]/g, '');

  if (sanitized === '') {
    if (preserveTextOnNaN) {
      return {
        storageValue: trimmed,
        displayValue: trimmed,
        numericValue: null,
        isNumeric: false,
        usedDefault: false,
        isEmpty: false,
      };
    }
    return applyDefault(false);
  }

  const parsed = Number(sanitized);
  if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
    if (preserveTextOnNaN) {
      return {
        storageValue: trimmed,
        displayValue: trimmed,
        numericValue: null,
        isNumeric: false,
        usedDefault: false,
        isEmpty: false,
      };
    }
    return applyDefault(false);
  }

  const normalizedNumber = Number.isInteger(parsed)
    ? Math.round(parsed)
    : Number(parsed.toFixed(2));

  return {
    storageValue: normalizedNumber,
    displayValue: normalizedNumber.toString(),
    numericValue: normalizedNumber,
    isNumeric: true,
    usedDefault: false,
    isEmpty: false,
  };
}

export function getCostDisplayValue(
  value: unknown,
  options: NormalizeCostOptions = {}
): string {
  return normalizeCost(value, options).displayValue;
}

